import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip, FormControl, Grid, InputLabel, LinearProgress, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DownloadIcon from '@mui/icons-material/Download';
import { Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { buildHistoryAnalysis, historyYears, variation } from './enrollmentHistoryModel';
import { loadEnrollmentHistory } from './enrollmentHistoryLoader';
import { campaignPeriod, displayDate, periodError } from './enrollmentPeriod';
import { gradeSort } from './enrollmentStatistics';
const metrics = {
  total: 'Total de matrículas',
  new: 'Novatos',
  returning: 'Veteranos',
  unknown: 'Tipo não informado'
};
const panel = {
  p: {
    xs: 2,
    md: 3
  },
  minWidth: 0,
  borderRadius: 3,
  border: '1px solid #e1e9f0',
  boxShadow: '0 4px 18px #173b5710'
};
const number = value => value === null || value === undefined ? '—' : value.toLocaleString('pt-BR');
const signed = value => value === null ? '—' : `${value > 0 ? '+' : ''}${number(value)}`;
const percent = value => value === null ? '—' : `${value > 0 ? '+' : ''}${value.toFixed(1).replace('.', ',')}%`;
const metricValue = (row, year, metric) => row.values[year] === null ? null : row.values[year][metric];
function HistoryTable({
  title,
  columns,
  rows
}) {
  return <Box sx={{
    minWidth: 0
  }}>
    <TableContainer sx={{
      overflowX: 'auto',
      border: '1px solid #e1e9f0',
      borderRadius: 2
    }}>
      <Table size="small" aria-label={title} sx={{
        minWidth: 760,
        '& th': {
          bgcolor: '#eff5fa',
          fontWeight: 700
        },
        '& td, & th': {
          py: 1.4,
          fontFamily: 'inherit'
        },
        '& tbody tr:nth-of-type(even)': {
          bgcolor: '#f8fafc'
        }
      }}>
        <TableHead><TableRow>{columns.map((column, index) => <TableCell key={column.key} align={index ? 'right' : 'left'}>{column.label}</TableCell>)}</TableRow></TableHead>
        <TableBody>{rows.map(row => <TableRow key={row.key}>{columns.map((column, index) => <TableCell key={column.key} align={index ? 'right' : 'left'} sx={{
              fontWeight: column.key === 'total' ? 700 : 400
            }}>{row[column.key]}</TableCell>)}</TableRow>)}</TableBody>
      </Table>
    </TableContainer>
    <Typography variant="caption" color="text.secondary" sx={{
      display: {
        xs: 'block',
        md: 'none'
      },
      mt: 1
    }}>Deslize a tabela para ver todos os anos.</Typography>
  </Box>;
}
function MatrixTable({
  title,
  rows,
  years,
  metric
}) {
  const columns = [{
    key: 'name',
    label: 'Segmento / turma'
  }, ...years.map(year => ({
    key: String(year),
    label: String(year)
  })), {
    key: 'delta',
    label: 'Variação no último ano'
  }, {
    key: 'percent',
    label: 'Variação %'
  }];
  const display = rows.map(row => {
    const delta = variation(metricValue(row, years[4], metric), metricValue(row, years[3], metric));
    return {
      key: row.key,
      name: row.name,
      ...Object.fromEntries(years.map(year => [year, number(metricValue(row, year, metric))])),
      delta: signed(delta.absolute),
      percent: percent(delta.percent)
    };
  });
  return <HistoryTable title={title} columns={columns} rows={display} />;
}
async function exportHistory(analysis, metric, segment, search, unit) {
  const XLSX = await import('xlsx');
  const book = XLSX.utils.book_new();
  const add = (name, rows) => {
    const sheet = XLSX.utils.aoa_to_sheet(rows);
    sheet['!cols'] = rows[0].map((_, index) => ({
      wch: index ? 20 : 45
    }));
    if (name === 'Resumo anual') rows.slice(1).forEach((_, index) => {
      const cell = sheet[`G${index + 2}`];
      if (cell && cell.t === 'n') cell.z = '0.0%';
    });
    if (name === 'Comparações') rows.slice(1).forEach((_, index) => {
      const cell = sheet[`F${index + 2}`];
      if (cell && cell.t === 'n') cell.z = '0.0%';
    });
    XLSX.utils.book_append_sheet(book, sheet, name);
  };
  add('Critérios', [['Evolução de matrículas', `${analysis.years[0]} a ${analysis.years[4]}`], ['Gerado em', new Date().toLocaleString('pt-BR')], ['Unidade', unit === 'campina' ? 'Campina Grande' : 'Patos'], ['Situações', 'Vigentes (1), encerrados (2) e pré-matrículas (6)'], ['Exclusões', 'Integral e contratos rescindidos'], ['Turmas', 'Cadastro oficial por ano e TurmaID; equivalência entre anos pelo nome sem o ano, preservando turma e turno'], ['Limites', analysis.period ? 'Filtro inclusivo pela DataMatricula. Mesmo mês/dia deslocado pela campanha; 29/02 vira 28/02 em ano não bissexto. Situação atual dos contratos, sem reconstrução de status no passado.' : 'Posição atual da base. Campanhas em andamento são parciais. Variação não equivale a comparação na mesma data.'], ['Segmento', segment || 'Todos'], ['Busca de turma', search || 'Todas'], ['Indicador das matrizes', metrics[metric]], ['Indisponível', 'Célula vazia significa consulta ou equivalência entre turmas não confirmada; zero significa base confirmada sem matrículas.']]);
  if (analysis.period) add('Períodos', [['Campanha', 'Início inclusivo', 'Fim inclusivo', 'Dias', 'Sem data válida (excluídos)', 'Ajuste bissexto'], ...analysis.annual.map(row => [row.year, row.range.start, row.range.end, row.range.days, row.excludedUndated, row.range.adjusted ? '29/02 ajustado para 28/02' : 'Não'])]);
  add('Resumo anual', [['Ano', 'Total', 'Novatos', 'Veteranos', 'Tipo não informado', 'Variação absoluta', 'Variação %', 'Situação da consulta'], ...analysis.annual.map(row => [row.year, row.total, row.new, row.returning, row.unknown, row.absolute, row.percent === null ? null : row.percent / 100, row.available ? 'Confirmada' : 'Não confirmada'])]);
  for (const [name, rows] of [['Segmentos', analysis.segments], ['Turmas', analysis.classrooms]]) {
    add(name, [['Nome', 'Segmento', 'Indicador', ...analysis.years], ...rows.flatMap(row => Object.entries(metrics).map(([key, label]) => [row.name, row.level, label, ...analysis.years.map(year => metricValue(row, year, key))]))]);
  }
  if (analysis.period) {
    add('Comparações', [['Campanha de referência', 'Campanha comparada', 'Total referência', 'Total comparado', 'Diferença total', 'Diferença %', 'Diferença novatos', 'Diferença veteranos'], ...analysis.comparisons.map(row => [analysis.years[4], row.year, row.selected, row.baseline, row.absolute, row.percent === null ? null : row.percent / 100, row.newDifference, row.returningDifference])]);
    add('Ritmo acumulado', [['Dia desde o início', ...analysis.years], ...analysis.curve.map(row => [row.day, ...analysis.years.map(year => row[year])])]);
  }
  XLSX.writeFile(book, `Evolucao_Matriculas_${unit}_${analysis.years[0]}_${analysis.years[4]}${analysis.period ? `_Periodo_${analysis.period.start}_${analysis.period.end}` : ''}.xlsx`);
}
export default function EnrollmentHistory({
  year,
  unit
}) {
  const scope = `${unit}:${year}`;
  const requestRef = useRef(0);
  const [state, setState] = useState({
    scope,
    snapshots: {},
    started: false,
    loading: false
  });
  const [segment, setSegment] = useState('');
  const [search, setSearch] = useState('');
  const [metric, setMetric] = useState('total');
  const [mode, setMode] = useState('global');
  const [draftPeriod, setDraftPeriod] = useState({
    start: '',
    end: ''
  });
  const [appliedPeriod, setAppliedPeriod] = useState(null);
  const [dateError, setDateError] = useState('');
  const [exportError, setExportError] = useState(false);
  const [exporting, setExporting] = useState(false);
  useEffect(() => {
    setState({
      scope,
      snapshots: {},
      started: false,
      loading: false
    });
    setMode('global');
    setDraftPeriod({
      start: '',
      end: ''
    });
    setAppliedPeriod(null);
    setDateError('');
    setSegment('');
    setSearch('');
    setExportError(false);
    return () => {
      requestRef.current++;
    };
  }, [scope]);
  const snapshots = state.scope === scope ? state.snapshots : {};
  const analysis = useMemo(() => buildHistoryAnalysis(snapshots, year, {
    segment,
    search,
    period: mode === 'period' ? appliedPeriod : null
  }), [snapshots, year, segment, search, mode, appliedPeriod]);
  const loading = state.scope === scope && state.loading;
  const started = state.scope === scope && state.started;
  const completed = Object.keys(snapshots).length;
  const success = analysis.annual.filter(row => row.available).length;
  const errors = analysis.years.filter(value => snapshots[value]?.error);
  const current = analysis.annual[4];
  const periodPending = mode === 'period' && (!appliedPeriod || draftPeriod.start !== appliedPeriod.start || draftPeriod.end !== appliedPeriod.end);
  const showResults = started && !periodPending && analysis.audit.valid;
  const groups = useMemo(() => {
    const map = new Map();
    analysis.classrooms.forEach(row => {
      if (!map.has(row.grade)) map.set(row.grade, []);
      map.get(row.grade).push(row);
    });
    return [...map.entries()].sort(([a], [b]) => gradeSort(a, b));
  }, [analysis]);
  const start = async force => {
    const request = ++requestRef.current;
    setState({
      scope,
      snapshots: {},
      started: true,
      loading: true
    });
    await loadEnrollmentHistory({
      years: historyYears(year),
      unit,
      force,
      isCancelled: () => request !== requestRef.current,
      onYear: (value, snapshot) => {
        if (request === requestRef.current) setState(old => old.scope === scope ? {
          ...old,
          snapshots: {
            ...old.snapshots,
            [value]: snapshot
          }
        } : old);
      }
    });
    if (request === requestRef.current) setState(old => ({
      ...old,
      loading: false
    }));
  };
  const applyDates = () => {
    const error = periodError(draftPeriod);
    setDateError(error);
    if (error) return;
    try {
      campaignPeriod(draftPeriod, year, year - 4);
    } catch (err) {
      setDateError(err.message);
      return;
    }
    setAppliedPeriod({
      ...draftPeriod
    });
    if (!started) start(false);
  };
  const download = async () => {
    setExportError(false);
    setExporting(true);
    try {
      await exportHistory(analysis, metric, segment, search, unit);
    } catch {
      setExportError(true);
    } finally {
      setExporting(false);
    }
  };
  return <Paper sx={panel}>
    <Box sx={{
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
      gap: 2,
      mb: 2
    }}>
      <Box><Typography variant="overline" color="primary">Visão gerencial · {unit === 'campina' ? 'Campina Grande' : 'Patos'} · {analysis.years[0]}—{year}</Typography><Typography variant="h5" sx={{
          fontWeight: 700
        }}>Evolução dos últimos 5 anos</Typography></Box>
      <Box sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 1
      }}>
        <Button variant={started ? 'outlined' : 'contained'} disabled={loading || !unit || periodPending} onClick={() => start(started && !errors.length)}>{started ? errors.length ? 'Tentar anos indisponíveis' : 'Atualizar histórico' : 'Gerar análise histórica'}</Button>
        {started && <Button startIcon={<DownloadIcon />} disabled={loading || exporting || !success || !showResults} onClick={download}>{exporting ? 'Exportando…' : 'Exportar Excel'}</Button>}
      </Box>
    </Box>
    <Typography variant="body2" color="text.secondary" sx={{
      mb: 2
    }}>Cinco anos letivos até a campanha selecionada, com o mesmo critério em todos os anos: vigentes, encerrados e pré-matrículas, sem integral ou rescindidos. O histórico consolidado pode diferir dos cartões acima, que usam a situação escolhida no relatório.</Typography>
    <Box sx={{
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'flex-start',
      gap: 2,
      mb: 2
    }}>
      <FormControl size="small" sx={{
        minWidth: 200
      }}><InputLabel id="history-analysis-mode">Tipo de análise</InputLabel><Select labelId="history-analysis-mode" label="Tipo de análise" value={mode} onChange={event => {
          setMode(event.target.value);
          setDateError('');
        }}><MenuItem value="global">Campanha completa</MenuItem><MenuItem value="period">Por período de matrícula</MenuItem></Select></FormControl>
      {mode === 'period' && <>
        <TextField type="date" size="small" label="Data inicial da matrícula" InputLabelProps={{
          shrink: true
        }} value={draftPeriod.start} onChange={event => {
          setDraftPeriod(old => ({
            ...old,
            start: event.target.value
          }));
          setDateError('');
        }} />
        <TextField type="date" size="small" label="Data final da matrícula" InputLabelProps={{
          shrink: true
        }} value={draftPeriod.end} onChange={event => {
          setDraftPeriod(old => ({
            ...old,
            end: event.target.value
          }));
          setDateError('');
        }} />
        <Button variant="contained" disabled={loading || !unit} onClick={applyDates}>Comparar período</Button>
      </>}
    </Box>
    {mode === 'period' && <Typography variant="body2" color="text.secondary" sx={{
      mb: 2
    }}>Informe as datas reais da campanha {year}, inclusive se começaram no ano anterior. As outras campanhas usam os mesmos meses e dias, deslocados pelo ano letivo. O primeiro e o último dia entram na contagem.</Typography>}
    {dateError && <Alert severity="error" sx={{
      mb: 2
    }}>{dateError}</Alert>}
    {periodPending && <Alert severity="info" sx={{
      mb: 2
    }}>Defina as datas e clique em Comparar período para atualizar os resultados.</Alert>}
    {!started && <Alert severity="info">Gere o histórico para consultar os cinco anos. A busca ocorre por campanha, com reaproveitamento das consultas confirmadas por até cinco minutos.</Alert>}
    {loading && <Box sx={{
      mb: 2
    }}><LinearProgress variant="determinate" value={completed / 5 * 100} /><Typography variant="caption">Conferindo turmas e matrículas: {completed} de 5 anos processados.</Typography></Box>}
    {!!errors.length && <Alert severity="warning" sx={{
      mb: 2
    }}>Anos não confirmados: {errors.join(', ')}. Valores indisponíveis aparecem como —; os demais anos permanecem disponíveis.</Alert>}
    {started && !analysis.audit.valid && <Alert severity="error">Os totais do histórico não fecharam. Atualize a consulta antes de usar ou exportar estes números.</Alert>}
    {exportError && <Alert severity="error" sx={{
      mb: 2
    }}>Não foi possível gerar o Excel. Tente novamente.</Alert>}
    {showResults && <Box sx={{
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr)',
      gap: 3
    }}>
      <Alert severity="info">{analysis.period ? `Período aplicado à campanha ${year}: ${displayDate(analysis.period.start)} a ${displayDate(analysis.period.end)}. Contagem pela data da matrícula, comparada aos intervalos equivalentes exibidos abaixo. A situação dos contratos é a atual: alterações posteriores podem modificar o histórico; estes dados não medem o retorno de uma ação publicitária específica.` : 'Este é um retrato atual dos registros de cada campanha, sem comparação pela mesma data de matrícula. Campanhas em andamento têm volumes parciais; a variação não representa projeção ou meta atingida.'}</Alert>
      {analysis.period && analysis.annual.some(row => row.excludedUndated > 0) && <Alert severity="warning">Matrículas sem data válida foram excluídas do recorte: {analysis.annual.filter(row => row.excludedUndated > 0).map(row => `${row.year}: ${row.excludedUndated}`).join('; ')}. Permanecem no modo Campanha completa.</Alert>}
      {analysis.period && analysis.annual.some(row => row.range.adjusted) && <Alert severity="info">Em anos não bissextos, 29/02 foi ajustado para 28/02. Confira as datas e a duração de cada intervalo na tabela.</Alert>}
      {Object.values(snapshots).some(value => value.unmatched || value.ambiguous || value.nameYearMismatch) && <Alert severity="warning">Há turmas sem correspondência no cadastro, com nomes repetidos ou com ano divergente no nome. O ano oficial do cadastro prevalece sobre o texto do nome. Os registros foram preservados; correspondências ambíguas ficam separadas.</Alert>}
      <Box sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 2
      }}>
        <FormControl size="small" sx={{
          minWidth: 190
        }}><InputLabel id="history-segment">Segmento</InputLabel><Select labelId="history-segment" label="Segmento" value={segment} onChange={event => setSegment(event.target.value)}><MenuItem value="">Todos os segmentos</MenuItem>{['Infantil', 'Fundamental I', 'Fundamental II', 'Ensino Médio', 'Outros / não informado'].map(value => <MenuItem value={value} key={value}>{value}</MenuItem>)}</Select></FormControl>
        <TextField size="small" label="Buscar turma ou turno no histórico" value={search} onChange={event => setSearch(event.target.value)} sx={{
          flex: 1,
          minWidth: 210
        }} />
        <FormControl size="small" sx={{
          minWidth: 185
        }}><InputLabel id="history-metric">Indicador das matrizes</InputLabel><Select labelId="history-metric" label="Indicador das matrizes" value={metric} onChange={event => setMetric(event.target.value)}>{Object.entries(metrics).map(([key, label]) => <MenuItem value={key} key={key}>{label}</MenuItem>)}</Select></FormControl>
      </Box>
      <Grid container spacing={2}>
        {[[`${analysis.period ? 'Matrículas no período' : 'Volume consolidado'} · ${year}`, number(current.total)], ...(analysis.period ? [['Novatos no período', number(current.new)], ['Veteranos no período', number(current.returning)]] : []), ['Diferença para o ano anterior', signed(current.absolute)], ['Variação de volume', percent(current.percent)], ['Participação de novatos', current.newShare === null ? '—' : `${current.newShare.toFixed(1).replace('.', ',')}%`]].map(([label, value]) => <Grid item xs={12} sm={6} md={analysis.period ? 4 : 3} key={label}><Box sx={{
            bgcolor: '#f0f6fb',
            p: 2,
            borderRadius: 2,
            height: '100%',
            boxSizing: 'border-box'
          }}><Typography variant="body2" color="text.secondary">{label}</Typography><Typography sx={{
              fontSize: 28,
              fontWeight: 700
            }}>{value}</Typography></Box></Grid>)}
      </Grid>
      {analysis.period && <Box><Typography variant="h6" sx={{
          fontWeight: 700,
          mb: 1
        }}>Ritmo acumulado dentro do período</Typography><Typography variant="body2" color="text.secondary" sx={{
          mb: 1
        }}>Acompanhe o ritmo de cada campanha desde o primeiro dia do intervalo, com os mesmos filtros. A curva exibe até 60 pontos e os dados podem ser exportados.</Typography><Box sx={{
          height: 330
        }} aria-hidden="true"><ResponsiveContainer width="100%" height="100%"><LineChart data={analysis.curve} margin={{
              right: 20,
              top: 10
            }}><CartesianGrid strokeDasharray="3 3" vertical={false} /><XAxis dataKey="day" tickFormatter={value => `Dia ${value}`} /><YAxis allowDecimals={false} width={45} /><Tooltip labelFormatter={value => `Dia ${value} desde o início`} /><Legend wrapperStyle={{
                fontSize: 12
              }} />{analysis.years.map((value, index) => <Line key={value} dataKey={String(value)} name={`Campanha ${value}`} stroke={['#1677cf', '#11a797', '#9b59b6', '#ef9b24', '#526579'][index]} strokeWidth={2} dot={false} connectNulls={false} isAnimationActive={false} />)}</LineChart></ResponsiveContainer></Box></Box>}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6} sx={{
          minWidth: 0
        }}><Typography sx={{
            fontWeight: 700,
            mb: 1
          }}>Volume de matrículas por ano</Typography><Box sx={{
            height: 270
          }} aria-hidden="true"><ResponsiveContainer width="100%" height="100%"><LineChart data={analysis.annual} margin={{
                left: 0,
                right: 18,
                top: 12,
                bottom: 4
              }}><CartesianGrid strokeDasharray="3 3" vertical={false} /><XAxis dataKey="year" /><YAxis allowDecimals={false} width={45} /><Tooltip /><Line dataKey="total" name="Total" stroke="#1677cf" strokeWidth={3} connectNulls={false} isAnimationActive={false} /></LineChart></ResponsiveContainer></Box></Grid>
        <Grid item xs={12} md={6} sx={{
          minWidth: 0
        }}><Typography sx={{
            fontWeight: 700,
            mb: 1
          }}>Composição: novatos e veteranos</Typography><Box sx={{
            height: 270
          }} aria-hidden="true"><ResponsiveContainer width="100%" height="100%"><BarChart data={analysis.annual} margin={{
                right: 12,
                top: 12
              }}><CartesianGrid strokeDasharray="3 3" vertical={false} /><XAxis dataKey="year" /><YAxis allowDecimals={false} width={45} /><Tooltip /><Legend wrapperStyle={{
                  fontSize: 12
                }} /><Bar dataKey="new" name="Novatos" stackId="types" fill="#9b59b6" isAnimationActive={false} /><Bar dataKey="returning" name="Veteranos" stackId="types" fill="#11a797" isAnimationActive={false} /><Bar dataKey="unknown" name="Não informado" stackId="types" fill="#94a3b8" isAnimationActive={false} /></BarChart></ResponsiveContainer></Box></Grid>
      </Grid>
      <Box><Typography variant="h6" sx={{
          mb: 1,
          fontWeight: 700
        }}>Resumo anual</Typography><HistoryTable title="Resumo anual dos cinco anos" columns={[{
          key: 'name',
          label: 'Ano letivo'
        }, ...(analysis.period ? [{
          key: 'range',
          label: 'Período inclusivo / duração'
        }] : []), {
          key: 'total',
          label: 'Total'
        }, {
          key: 'new',
          label: 'Novatos'
        }, {
          key: 'returning',
          label: 'Veteranos'
        }, {
          key: 'unknown',
          label: 'Não informado'
        }, {
          key: 'delta',
          label: 'Variação anual'
        }, {
          key: 'percent',
          label: 'Variação %'
        }]} rows={analysis.annual.map(row => ({
          key: row.year,
          name: `${row.year}${row.available ? '' : ' · não confirmado'}`,
          range: row.range ? `${displayDate(row.range.start)} a ${displayDate(row.range.end)} · ${row.range.days} dias` : '',
          total: number(row.total),
          new: number(row.new),
          returning: number(row.returning),
          unknown: number(row.unknown),
          delta: signed(row.absolute),
          percent: percent(row.percent)
        }))} /></Box>
      {analysis.period && <Box><Typography variant="h6" sx={{
          fontWeight: 700,
          mb: 1
        }}>Campanha {year} em relação aos outros anos</Typography><HistoryTable title="Comparação do período com as campanhas anteriores" columns={[{
          key: 'name',
          label: 'Campanha comparada'
        }, {
          key: 'selected',
          label: `Total ${year}`
        }, {
          key: 'baseline',
          label: 'Total comparado'
        }, {
          key: 'difference',
          label: 'Diferença de matrículas'
        }, {
          key: 'percent',
          label: 'Diferença %'
        }, {
          key: 'new',
          label: 'Diferença de novatos'
        }, {
          key: 'returning',
          label: 'Diferença de veteranos'
        }]} rows={analysis.comparisons.map(row => ({
          key: row.year,
          name: row.year,
          selected: number(row.selected),
          baseline: number(row.baseline),
          difference: signed(row.absolute),
          percent: percent(row.percent),
          new: signed(row.newDifference),
          returning: signed(row.returningDifference)
        }))} /></Box>}
      <Box><Typography variant="h6" sx={{
          mb: 1,
          fontWeight: 700
        }}>Evolução por segmento · {metrics[metric]}</Typography><MatrixTable title="Evolução por segmento" rows={analysis.segments} years={analysis.years} metric={metric} /></Box>
      <Box><Typography variant="h6" sx={{
          mb: 1,
          fontWeight: 700
        }}>Evolução por turma · {metrics[metric]}</Typography><Typography variant="body2" color="text.secondary" sx={{
          mb: 2
        }}>Abra a série para comparar as turmas. A correspondência entre anos preserva série, letra e turno; não representa o acompanhamento dos mesmos alunos.</Typography>
        {groups.map(([grade, rows]) => <Accordion key={grade} disableGutters sx={{
          boxShadow: 'none',
          border: '1px solid #e1e9f0',
          '&:before': {
            display: 'none'
          }
        }}><AccordionSummary expandIcon={<ExpandMoreIcon />}><Box sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1
            }}><Typography sx={{
                fontWeight: 700
              }}>{grade}</Typography><Chip size="small" label={`${rows.length} turmas no histórico`} /></Box></AccordionSummary><AccordionDetails><MatrixTable title={`Evolução das turmas de ${grade}`} rows={rows} years={analysis.years} metric={metric} /></AccordionDetails></Accordion>)}
        {!groups.length && <Typography color="text.secondary">Nenhuma turma corresponde aos filtros nos anos confirmados.</Typography>}
      </Box>
      <Accordion disableGutters sx={{
        boxShadow: 'none',
        bgcolor: '#f7fafc'
      }}><AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="body2">Critérios e conferência da coleta · {success}/5 anos confirmados</Typography></AccordionSummary><AccordionDetails><Typography variant="body2" color="text.secondary">Turmas consultadas no cadastro oficial de cada ano e vinculadas pelo TurmaID. Contratos duplicados são eliminados pelo contrato e aluno. Turmas com zero matrículas permanecem no histórico quando presentes no cadastro. Tipos não reconhecidos continuam no total. Percentual de variação não é calculado quando a base anterior é zero ou não foi confirmada.</Typography>{analysis.years.map(value => <Typography key={value} variant="caption" component="div">{value}: {snapshots[value]?.queriedAt ? `consultado em ${new Date(snapshots[value].queriedAt).toLocaleString('pt-BR')}, início letivo ${snapshots[value].date.split('/').reverse().join('/')}` : 'consulta não confirmada'}</Typography>)}</AccordionDetails></Accordion>
    </Box>}
  </Paper>;
}
